import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, Lock, User, ArrowLeft, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { consumeRedirect, peekRedirect } from "@/lib/authRedirect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transformă orice detaliu de eroare (string / array 422 / obiect) în text.
function formatEroare(detail) {
  if (detail == null) return "A apărut o eroare. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, login, register, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteGroup, setInviteGroup] = useState(null);
  const [inviteGroupId, setInviteGroupId] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate(consumeRedirect(), { replace: true });
  }, [user, loading, navigate]);

  // Dacă venim dintr-un link de invitație, arătăm numele grupului.
  useEffect(() => {
    const dest = peekRedirect();
    const match = dest && dest.match(/^\/invita\/([^/?]+)/);
    if (!match) return;
    setInviteGroupId(match[1]);
    (async () => {
      try {
        const r = await axios.get(`${API}/groups/${match[1]}/public-name`);
        setInviteGroup(r.data.name);
        setMode("register");
      } catch {
        // grup inexistent — ignorăm banner-ul
      }
    })();
  }, []);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        // Redirect-ul e făcut O SINGURĂ dată în useEffect (când user devine setat),
        // ca să nu consumăm de două ori destinația salvată (invitație).
      } else if (mode === "register") {
        await register(email.trim(), password, name.trim(), inviteGroupId);
      } else {
        await forgotPassword(email.trim());
        toast.success(
          "Dacă emailul există, vei primi un link de resetare. Nu-l vezi în Inbox? Verifică și Spam/Promoții.",
          { duration: 6000 }
        );
        setMode("login");
      }
    } catch (err) {
      toast.error(formatEroare(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const titlu =
    mode === "login"
      ? "Bine ai revenit"
      : mode === "register"
      ? "Creează cont"
      : "Ai uitat parola?";
  const subtitlu =
    mode === "login"
      ? "Intră în cont ca să-ți găsești grupurile pe orice telefon."
      : mode === "register"
      ? "Câteva secunde și ești gata să împarți cheltuielile."
      : "Îți trimitem pe email un link ca să setezi o parolă nouă.";

  return (
    <div className="min-h-screen flex flex-col justify-center px-8 py-10 bg-background">
      <div className="w-full max-w-xs mx-auto">
        {inviteGroup && (
          <div
            data-testid="invite-banner"
            className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3"
          >
            <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <Users className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
            </div>
            <p className="text-sm text-emerald-800 leading-snug">
              Ai fost invitat în grupul{" "}
              <span className="font-bold" data-testid="invite-group-name">
                „{inviteGroup}”
              </span>
              . Autentifică-te ca să te alături.
            </p>
          </div>
        )}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mb-5">
            <img
              src="/chit-logo.png"
              alt="Chit"
              data-testid="app-logo"
              className="h-14"
            />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">
            {titlu}
          </h1>
          <p className="text-slate-500 text-sm mt-2 leading-relaxed">{subtitlu}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <User className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="auth-name-input"
                type="text"
                placeholder="Numele tău"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full h-12 rounded-xl border border-slate-200 pl-11 pr-4 text-sm outline-none focus:border-emerald-500"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="auth-email-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-12 rounded-xl border border-slate-200 pl-11 pr-4 text-sm outline-none focus:border-emerald-500"
            />
          </div>

          {mode !== "forgot" && (
            <div className="relative">
              <Lock className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="auth-password-input"
                type="password"
                placeholder="Parolă"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full h-12 rounded-xl border border-slate-200 pl-11 pr-4 text-sm outline-none focus:border-emerald-500"
              />
            </div>
          )}

          {mode === "login" && (
            <div className="text-right">
              <button
                type="button"
                data-testid="forgot-link"
                onClick={() => setMode("forgot")}
                className="text-xs font-semibold text-emerald-600 hover:underline"
              >
                Ai uitat parola?
              </button>
            </div>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-2xl bg-emerald-600 text-white font-bold text-base flex items-center justify-center gap-2 hover:bg-emerald-700 active:scale-95 transition-transform disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "login"
              ? "Intră în cont"
              : mode === "register"
              ? "Creează cont"
              : "Trimite linkul"}
          </button>
        </form>

        {mode === "forgot" ? (
          <button
            data-testid="back-to-login"
            onClick={() => setMode("login")}
            className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" /> Înapoi la autentificare
          </button>
        ) : (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400">sau</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <button
              data-testid="google-login-btn"
              onClick={handleGoogle}
              className="w-full h-12 rounded-2xl bg-white border border-slate-200 shadow-sm text-slate-800 font-bold text-sm flex items-center justify-center gap-3 hover:bg-slate-50 active:scale-95 transition-transform"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="h-5 w-5"
              />
              Continuă cu Google
            </button>

            <p className="text-center text-sm text-slate-500 mt-6">
              {mode === "login" ? "Nu ai cont?" : "Ai deja cont?"}{" "}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="font-bold text-emerald-600 hover:underline"
              >
                {mode === "login" ? "Înregistrează-te" : "Autentifică-te"}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
